import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { downloadRowsAsXlsx } from "../../utils/excelDownload";

function todayIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function monthStart(date) {
  return `${date.slice(0, 8)}01`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata",
  });
}

function labelForPurpose(value) {
  return ({ security_gate: "Stopped at security gate", reception: "Stopped at reception", management: "Met the management" })[value] || "Institution visit";
}

function labelForStatus(value) {
  return String(value || "warm").replace(/^./, char => char.toUpperCase());
}

const cardStyle = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 18,
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
};

export default function MyVisitReports({ accent = "#ef1f35" }) {
  const today = useMemo(todayIST, []);
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [data, setData] = useState({ summary: {}, visits: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [photo, setPhoto] = useState(null);
  const photoUrlRef = useRef(null);

  const closePhoto = useCallback(() => {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = null;
    setPhoto(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/reports/my-visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      setData({ summary: res.data?.summary || {}, visits: res.data?.visits || [] });
    } catch (err) {
      setError(err.response?.data?.error || "Could not load your visit report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current); }, []);

  const showPhoto = async (visit) => {
    try {
      closePhoto();
      const res = await API.get(`/visits/${visit.id}/photo`, { responseType: "blob" });
      if (!res.data || res.data.size === 0) throw new Error("Empty photo");
      const url = URL.createObjectURL(res.data);
      photoUrlRef.current = url;
      setPhoto({ url, title: visit.institution_name || "Visit photo" });
    } catch {
      toast.error("The visit photo is not available right now.");
    }
  };

  const download = async () => {
    const rows = data.visits.map(visit => [
      String(visit.date || "").slice(0, 10),
      visit.institution_name || "",
      visit.institution_type || "",
      visit.city || "",
      labelForPurpose(visit.visit_purpose),
      labelForStatus(visit.lead_status),
      visit.lead_stage || "",
      visit.next_follow_up_date || "",
      visit.is_primary_visit ? "My visit" : "Visited with a companion",
      visit.latitude != null && visit.longitude != null ? `https://www.google.com/maps?q=${visit.latitude},${visit.longitude}` : "",
      visit.comments || "",
    ]);
    await downloadRowsAsXlsx({
      filename: `my-visit-report-${from}-to-${to}.xlsx`,
      sheetName: "My visits",
      title: "My Visit Report",
      subtitleRows: [`Period: ${from} to ${to}`, `Visits: ${data.summary?.visits || 0} | Institutions: ${data.summary?.institutions || 0} | Route KM: ${Number(data.summary?.total_km || 0).toFixed(1)}`],
      headers: ["Date", "Institution", "Institution type", "City", "Visit outcome", "Lead status", "Lead stage", "Next follow-up", "Participation", "Map link", "Meeting notes"],
      rows,
    });
  };

  const setPreset = (preset) => {
    if (preset === "month") { setFrom(monthStart(today)); setTo(today); }
    if (preset === "lastMonth") {
      const firstOfThisMonth = new Date(`${monthStart(today)}T00:00:00`);
      const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 86400000);
      const lastMonthEndIso = lastMonthEnd.toLocaleDateString("en-CA");
      setFrom(monthStart(lastMonthEndIso));
      setTo(lastMonthEndIso);
    }
  };

  const grouped = data.visits.reduce((all, visit) => {
    const date = String(visit.date || "").slice(0, 10);
    if (!all[date]) all[date] = [];
    all[date].push(visit);
    return all;
  }, {});

  return (
    <section style={{ padding: "clamp(12px, 2vw, 28px)", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ color: accent, fontWeight: 900, letterSpacing: ".08em", fontSize: 12 }}>FIELD ACTIVITY</div>
          <h2 style={{ margin: "5px 0 6px", fontSize: "clamp(25px, 3vw, 34px)", color: "#102a43" }}>My Visit Reports</h2>
          <p style={{ margin: 0, color: "#64748b" }}>Your institutions, follow-ups, route distance and field evidence in one place.</p>
        </div>
        <button onClick={download} disabled={!data.visits.length} style={{ alignSelf: "center", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 800, cursor: data.visits.length ? "pointer" : "not-allowed", color: "#fff", background: data.visits.length ? accent : "#94a3b8" }}>↓ Download Excel</button>
      </div>

      <div style={{ ...cardStyle, marginBottom: 18, display: "flex", alignItems: "end", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#334155" }}>From date
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 9, font: "inherit" }} />
        </label>
        <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#334155" }}>To date
          <input type="date" value={to} min={from} max={today} onChange={e => setTo(e.target.value)} style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 9, font: "inherit" }} />
        </label>
        <button onClick={() => setPreset("month")} style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#f8fafc", fontWeight: 750, cursor: "pointer" }}>This month</button>
        <button onClick={() => setPreset("lastMonth")} style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#f8fafc", fontWeight: 750, cursor: "pointer" }}>Last month</button>
        <button onClick={load} style={{ padding: "10px 14px", borderRadius: 9, border: "none", color: "#fff", background: "#0f766e", fontWeight: 800, cursor: "pointer" }}>Refresh</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 18 }}>
        {[
          ["Institutions visited", data.summary?.institutions || 0, "#0f766e"],
          ["Visit records", data.summary?.visits || 0, accent],
          ["Active field days", data.summary?.active_days || 0, "#2563eb"],
          ["Route distance", `${Number(data.summary?.total_km || 0).toFixed(1)} km`, "#7c3aed"],
        ].map(([label, value, color]) => <div key={label} style={{ ...cardStyle, borderTop: `4px solid ${color}` }}><div style={{ color: "#64748b", fontWeight: 750, fontSize: 13 }}>{label}</div><div style={{ marginTop: 7, color, fontSize: 28, fontWeight: 900 }}>{value}</div></div>)}
      </div>

      {error && <div style={{ ...cardStyle, borderColor: "#fecaca", color: "#b91c1c", marginBottom: 16, fontWeight: 750 }}>{error}</div>}
      {loading ? <div style={{ ...cardStyle, color: "#64748b" }}>Loading your field activity…</div> : !data.visits.length ? <div style={{ ...cardStyle, textAlign: "center", padding: 42, color: "#64748b" }}><div style={{ fontSize: 36 }}>📍</div><h3 style={{ color: "#334155" }}>No visits in this period</h3><p>Once a visit is saved, it will appear here with its location and follow-up details.</p></div> : Object.entries(grouped).map(([date, visits]) => (
        <section key={date} style={{ marginBottom: 20 }}>
          <h3 style={{ margin: "0 0 9px", color: "#334155", fontSize: 16 }}>{formatDate(date)} <span style={{ color: "#94a3b8", fontWeight: 600 }}>· {visits.length} visit{visits.length === 1 ? "" : "s"}</span></h3>
          <div style={{ display: "grid", gap: 10 }}>
            {visits.map(visit => {
              const open = openId === visit.id;
              const hasMap = visit.latitude != null && visit.longitude != null;
              return <article key={visit.id} style={cardStyle}>
                <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "start", flexWrap: "wrap" }}>
                  <div>
                    <h4 style={{ margin: 0, color: "#102a43", fontSize: 18 }}>{visit.institution_name}</h4>
                    <p style={{ margin: "5px 0 0", color: "#64748b" }}>{[visit.institution_type, visit.city].filter(Boolean).join(" · ") || "Institution details pending"}</p>
                  </div>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    <span style={{ background: visit.is_primary_visit ? "#dcfce7" : "#e0f2fe", color: visit.is_primary_visit ? "#166534" : "#0369a1", borderRadius: 999, padding: "5px 9px", fontSize: 12, fontWeight: 850 }}>{visit.is_primary_visit ? "My visit" : "Visited with companion"}</span>
                    <span style={{ background: "#fff7ed", color: "#c2410c", borderRadius: 999, padding: "5px 9px", fontSize: 12, fontWeight: 850 }}>{labelForStatus(visit.lead_status)}</span>
                  </div>
                </div>
                <div style={{ marginTop: 13, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: "#475569", fontWeight: 700 }}>◎ {labelForPurpose(visit.visit_purpose)}</span>
                  {visit.created_at && <span style={{ color: "#64748b" }}>· saved {formatTime(visit.created_at)}</span>}
                  {visit.next_follow_up_date && <span style={{ color: "#7c3aed", fontWeight: 750 }}>· follow-up {formatDate(visit.next_follow_up_date)}</span>}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                  <button onClick={() => setOpenId(open ? null : visit.id)} style={{ padding: "8px 11px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", fontWeight: 750, cursor: "pointer" }}>{open ? "Hide details" : "View details"}</button>
                  {hasMap && <button onClick={() => window.open(`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`, "_blank", "noopener,noreferrer")} style={{ padding: "8px 11px", border: "1px solid #bfdbfe", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", fontWeight: 750, cursor: "pointer" }}>View location</button>}
                  {visit.photo_url && <button onClick={() => showPhoto(visit)} style={{ padding: "8px 11px", border: "1px solid #bbf7d0", borderRadius: 8, background: "#f0fdf4", color: "#15803d", fontWeight: 750, cursor: "pointer" }}>View photo</button>}
                </div>
                {open && <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e2e8f0", display: "grid", gap: 8, color: "#475569" }}>
                  {visit.comments && <div><b style={{ color: "#334155" }}>Meeting notes:</b> {visit.comments}</div>}
                  {visit.action_required && <div><b style={{ color: "#334155" }}>Next action:</b> {String(visit.action_required).replaceAll("_", " ")}</div>}
                  {visit.companions?.length > 0 && <div><b style={{ color: "#334155" }}>Travelled with:</b> {visit.companions.map(person => person.name).join(", ")}</div>}
                  {!visit.comments && !visit.action_required && !visit.companions?.length && <div>No additional details were recorded for this visit.</div>}
                </div>}
              </article>;
            })}
          </div>
        </section>
      ))}

      {photo && <div role="dialog" aria-modal="true" onClick={closePhoto} style={{ position: "fixed", inset: 0, zIndex: 1000, display: "grid", placeItems: "center", padding: 20, background: "rgba(15,23,42,.76)" }}>
        <div onClick={e => e.stopPropagation()} style={{ maxWidth: "min(92vw, 760px)", maxHeight: "90vh", background: "#fff", padding: 14, borderRadius: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10, fontWeight: 850, color: "#102a43" }}><span>{photo.title}</span><button onClick={closePhoto} style={{ border: "none", background: "#e2e8f0", borderRadius: 7, padding: "5px 9px", cursor: "pointer" }}>Close</button></div>
          <img src={photo.url} alt={photo.title} style={{ display: "block", maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 10 }} />
        </div>
      </div>}
    </section>
  );
}
